<template>
  <div class="power-contract-editor" ref="editorRoot">
    <!-- Garrett: staff kept looking for the mileage in these boxes -->
    <div class="notification is-info placement-only-note">
      <i class="fas fa-info-circle"></i> Fields are for placement only. They fill in when you do the final preview.
    </div>
    <div class="notification is-warning mb-10" v-if="missingNames.length > 0">
      <i class="fas fa-exclamation-triangle"></i> Not found automatically, drag these boxes onto their lines (they start at the top of page 1): <strong>{{ missingNames.join(', ') }}</strong>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div v-else-if="loading" class="loading-message">
      Loading Power Contract
      <div class="has-text-centered mt-20">
        <img src="@/assets/ajax-loader.gif">
      </div>
    </div>
    <div class="placement-pages" v-show="!loading && !error">
      <div class="placement-page-container" v-for="pageInfo in pageInfos" :key="'PCP' + pageInfo.number">
        <p class="placement-page-number">Page {{ pageInfo.number }} of {{ pageInfos.length }}</p>
        <div class="placement-page" :ref="'page' + pageInfo.number">
          <canvas :ref="'canvas' + pageInfo.number" class="placement-canvas"></canvas>
          <div class="placement-watermark">PLACEMENT ONLY</div>
          <div
            class="placement-box"
            v-for="field in getPageFields(pageInfo.number)"
            :key="field.id"
            :data-field-id="field.id"
            :class="{'is-active': activeFieldId === field.id, 'is-readonly': readOnly}"
            :style="getBoxStyle(field)"
            :title="getTypeLabel(field.type) + (field.auto ? ' (placed automatically)' : '')"
            @click.stop
            @mousedown.stop.prevent="onBoxMouseDown($event, field)">
            <span class="placement-box-label" :style="{ backgroundColor: getTypeColor(field.type) }">{{ getTypeLabel(field.type) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const FIELD_TYPES = [
  { type: 'signature', label: 'Customer signature', color: 'rgb(32, 156, 238)', width: 100, height: 24 },
  { type: 'date', label: 'Customer date', color: 'rgb(0, 166, 140)', width: 110, height: 18 },
  { type: 'initials', label: 'Initials', color: 'rgb(132, 74, 201)', width: 40, height: 20 },
  { type: 'dealerSignature', label: 'Dealer /S/ (auto)', color: 'rgb(230, 126, 34)', width: 100, height: 22 },
  { type: 'dealerDate', label: 'Dealer date (auto)', color: 'rgb(150, 95, 60)', width: 110, height: 18 },
  { type: 'mileage', label: 'Mileage (auto)', color: 'rgb(96, 125, 139)', width: 90, height: 14 },
  { type: 'startDate', label: 'Start date (auto)', color: 'rgb(96, 125, 139)', width: 90, height: 14 },
  { type: 'endDate', label: 'End date (auto)', color: 'rgb(96, 125, 139)', width: 90, height: 14 }
]

const FALLBACK_WIDTH = 900

export default {
  name: 'PowerContractPlacementEditor',
  props: {
    pdfUrl: {
      type: String,
      default: ''
    },
    value: {
      type: Array,
      default: () => []
    },
    pages: {
      type: Array,
      default: () => []
    },
    missing: {
      type: Array,
      default: () => []
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    // off while the date fields above the editor are showing, so the scroll doesn't carry them out of view
    autoScroll: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      loading: true,
      error: null,
      pageInfos: [],
      pageScales: {},
      activeFieldId: '',
      dragState: null,
      loadToken: 0
    }
  },
  computed: {
    missingNames: function () {
      return (this.missing || []).map((x) => this.getTypeLabel(x))
    }
  },
  methods: {
    getTypeDefinition: function (type) {
      return FIELD_TYPES.find((x) => x.type === type) || { type: type, label: type, color: 'rgb(120, 120, 120)', width: 100, height: 20 }
    },
    getTypeLabel: function (type) {
      return this.getTypeDefinition(type).label
    },
    getTypeColor: function (type) {
      return this.getTypeDefinition(type).color
    },
    getPageFields: function (pageNumber) {
      return (this.value || []).filter((x) => x.page === pageNumber)
    },
    getPageInfo: function (pageNumber) {
      return this.pageInfos.find((x) => x.number === pageNumber) || this.pages.find((x) => x.number === pageNumber) || null
    },
    getScale: function (pageNumber) {
      return this.pageScales[pageNumber] || 1
    },
    getBoxStyle: function (field) {
      let scale = this.getScale(field.page)
      let color = this.getTypeColor(field.type)
      return {
        left: (field.x * scale) + 'px',
        top: (field.y * scale) + 'px',
        width: (field.width * scale) + 'px',
        height: (field.height * scale) + 'px',
        borderColor: color,
        backgroundColor: color.replace('rgb(', 'rgba(').replace(')', ', 0.18)')
      }
    },
    getPageElement: function (pageNumber) {
      let ref = this.$refs['page' + pageNumber]
      return Array.isArray(ref) ? ref[0] : ref
    },
    getCanvasElement: function (pageNumber) {
      let ref = this.$refs['canvas' + pageNumber]
      return Array.isArray(ref) ? ref[0] : ref
    },
    cloneFields: function () {
      return (this.value || []).map((x) => Object.assign({}, x))
    },
    clamp: function (value, min, max) {
      return Math.min(Math.max(value, min), Math.max(min, max))
    },
    async loadPdf () {
      let token = ++this.loadToken
      this.loading = true
      this.error = null
      this.pageInfos = []
      this.pageScales = {}
      this.pdfDocument = null
      if (!this.pdfUrl) {
        this.loading = false
        this.error = 'The Power Contract file could not be found.'
        return
      }
      try {
        const response = await fetch(this.pdfUrl)
        if (!response.ok) {
          throw new Error('Download failed with status ' + response.status)
        }
        const pdfBytes = await response.arrayBuffer()
        const pdfDocument = await this.$pdf.getDocument({ data: pdfBytes }).promise
        if (token !== this.loadToken) return
        // keep the pdfjs document off the reactive data
        this.pdfDocument = pdfDocument
        let pageInfos = []
        for (let i = 1; i <= pdfDocument.numPages; i++) {
          const page = await pdfDocument.getPage(i)
          const viewport = page.getViewport({ scale: 1 })
          pageInfos.push({ number: i, width: viewport.width, height: viewport.height })
        }
        if (token !== this.loadToken) return
        this.pageInfos = pageInfos
        this.addMissingFields()
        this.loading = false
        await this.$nextTick()
        await this.renderAllPages(token)
      } catch (err) {
        console.error('Error loading Power Contract:', err)
        if (token !== this.loadToken) return
        this.error = 'Failed to load the Power Contract. Please try again.'
        this.loading = false
      }
    },
    async renderAllPages (token) {
      const firstField = this.getFirstField()
      for (let i = 0; i < this.pageInfos.length; i++) {
        if (token !== this.loadToken) return
        await this.renderPage(this.pageInfos[i].number)
        // pages render in order, so once the first field's page is drawn its position is final
        if (this.autoScroll && firstField && firstField.page === this.pageInfos[i].number) {
          await this.$nextTick()
          this.scrollToField(firstField)
        }
      }
    },
    getFirstField: function () {
      // missing fields are parked on page 1, skip them so the scroll lands on a found field when there is one
      let fields = (this.value || []).filter((x) => x.auto !== false || !(this.missing || []).includes(x.type))
      if (fields.length === 0) fields = this.value || []
      return fields.slice().sort((a, b) => a.page - b.page || a.y - b.y)[0] || null
    },
    scrollToField: function (field) {
      const box = this.$el.querySelector('[data-field-id="' + field.id + '"]')
      if (box && box.scrollIntoView) {
        box.scrollIntoView({ block: 'center' })
      }
    },
    addMissingFields: function () {
      // fields the auto-find missed are parked at the top of page 1 for the user to drag into place
      const firstPage = this.pageInfos[0]
      if (!firstPage) return
      const missingTypes = (this.missing || []).filter((type) => !(this.value || []).some((x) => x.type === type))
      if (missingTypes.length === 0) return
      let fields = this.cloneFields()
      missingTypes.forEach((type, index) => {
        const definition = this.getTypeDefinition(type)
        fields.push({
          id: 'pc_' + this.$uuid.v4(),
          type: type,
          page: firstPage.number,
          x: 20,
          y: this.round(Math.min(20 + index * 40, firstPage.height - definition.height)),
          width: definition.width,
          height: definition.height,
          anchor: '',
          auto: false
        })
      })
      this.$emit('input', fields)
    },
    async renderPage (pageNumber) {
      const canvas = this.getCanvasElement(pageNumber)
      const wrapper = this.getPageElement(pageNumber)
      if (!canvas || !wrapper || !this.pdfDocument) return
      const page = await this.pdfDocument.getPage(pageNumber)
      const baseViewport = page.getViewport({ scale: 1 })
      // a hidden tab measures 0 wide - render at a sensible width, the 100% css width stretches it
      const displayWidth = wrapper.clientWidth || FALLBACK_WIDTH
      const renderScale = displayWidth / baseViewport.width * (window.devicePixelRatio || 1)
      const viewport = page.getViewport({ scale: renderScale })
      canvas.width = viewport.width
      canvas.height = viewport.height
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport }).promise
      this.updateScale(pageNumber)
    },
    updateScale: function (pageNumber) {
      const wrapper = this.getPageElement(pageNumber)
      const pageInfo = this.getPageInfo(pageNumber)
      if (!wrapper || !pageInfo || !pageInfo.width) return
      const width = wrapper.clientWidth
      if (!width) return
      this.$set(this.pageScales, pageNumber, width / pageInfo.width)
    },
    updateScales: function () {
      this.pageInfos.forEach((x) => this.updateScale(x.number))
    },
    onBoxMouseDown: function (event, field) {
      if (this.readOnly || event.button !== 0) return
      const pageInfo = this.getPageInfo(field.page)
      if (!pageInfo) return
      this.updateScale(field.page)
      this.activeFieldId = field.id
      this.dragState = {
        fieldId: field.id,
        startX: event.clientX,
        startY: event.clientY,
        scale: this.getScale(field.page),
        pageWidth: pageInfo.width,
        pageHeight: pageInfo.height,
        original: Object.assign({}, field)
      }
      window.addEventListener('mousemove', this.onWindowMouseMove)
      window.addEventListener('mouseup', this.onWindowMouseUp)
    },
    onWindowMouseMove: function (event) {
      const state = this.dragState
      if (!state) return
      const dx = (event.clientX - state.startX) / state.scale
      const dy = (event.clientY - state.startY) / state.scale
      const original = state.original
      let fields = this.cloneFields()
      let target = fields.find((x) => x.id === state.fieldId)
      if (!target) return
      target.x = this.round(this.clamp(original.x + dx, 0, state.pageWidth - original.width))
      target.y = this.round(this.clamp(original.y + dy, 0, state.pageHeight - original.height))
      if (target.x === original.x && target.y === original.y) return
      this.$emit('input', fields)
    },
    onWindowMouseUp: function () {
      this.dragState = null
      this.removeWindowListeners()
    },
    removeWindowListeners: function () {
      window.removeEventListener('mousemove', this.onWindowMouseMove)
      window.removeEventListener('mouseup', this.onWindowMouseUp)
    },
    round: function (value) {
      return Math.round(value * 10) / 10
    }
  },
  watch: {
    pdfUrl: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.loadPdf()
      }
    }
  },
  mounted: function () {
    this.pdfDocument = null
    // a resize of the editor (window resize, or the tab becoming visible) changes the point to pixel scale
    if (typeof ResizeObserver !== 'undefined' && this.$refs.editorRoot) {
      this.resizeObserver = new ResizeObserver(() => this.updateScales())
      this.resizeObserver.observe(this.$refs.editorRoot)
    } else {
      window.addEventListener('resize', this.updateScales)
    }
    this.loadPdf()
  },
  beforeDestroy: function () {
    this.loadToken++
    this.removeWindowListeners()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    } else {
      window.removeEventListener('resize', this.updateScales)
    }
    if (this.pdfDocument && this.pdfDocument.destroy) {
      this.pdfDocument.destroy()
    }
    this.pdfDocument = null
  }
}
</script>

<style scoped>
  /* stays in view under the modal's sticky tabs while the pages scroll */
  .placement-only-note {
    position: sticky;
    top: 30px;
    z-index: 4;
    padding: 8px 12px;
    margin-bottom: 10px;
  }

  .placement-watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-30deg);
    font-size: 4rem;
    font-weight: 700;
    letter-spacing: 0.3rem;
    white-space: nowrap;
    color: rgba(255, 56, 96, 0.12);
    pointer-events: none;
  }

  .placement-page-container {
    margin-bottom: 16px;
  }

  .placement-page-number {
    font-size: 0.75rem;
    color: #7a7a7a;
    margin-bottom: 2px;
  }

  .placement-page {
    position: relative;
    width: 100%;
    border: 1px solid #dbdbdb;
    box-shadow: 0 1px 4px rgba(10, 10, 10, 0.1);
    user-select: none;
  }

  .placement-canvas {
    display: block;
    width: 100%;
  }

  .placement-box {
    position: absolute;
    border: 2px solid;
    border-radius: 2px;
    cursor: move;
    box-sizing: border-box;
    &.is-active {
      box-shadow: 0 0 0 2px rgba(255, 221, 87, 0.9);
    }
    &.is-readonly {
      cursor: default;
    }
  }

  .placement-box-label {
    position: absolute;
    left: -2px;
    bottom: 100%;
    color: white;
    font-size: 10px;
    line-height: 14px;
    padding: 0 4px;
    white-space: nowrap;
    border-radius: 2px 2px 0 0;
    pointer-events: none;
  }

  .error-message {
    color: red;
    font-weight: bold;
    padding: 20px;
    text-align: center;
  }

  .loading-message {
    font-style: italic;
    padding: 20px;
    text-align: center;
    min-height: 200px;
  }
</style>
